import { useEffect, useRef } from "react";

import { Box, Flex } from "@similarweb/ui-components/foundations";

import useCurrentStateAndParamsWithFixedDomain from "app/pages/sales-intelligence/modules/company-analysis/hooks/useCurrentStateAndParamsWithFixedDomain";
import { useGeoBreakdownQuery } from "app/pages/sales-intelligence/modules/company-analysis/queries/useGeoBreakdownQuery";
import { IndustryTaxonomyPromoBanner } from "app/pages/sales-intelligence/pages/company-analysis/components/IndustryTaxonomyPromoBanner/IndustryTaxonomyPromoBanner";
import { proNavigator } from "single-spa/react-app/ProNavigator";

import CompanyCardWithErrorBoundary from "../CompanyCard/CompanyCardWithErrorBoundary";
import CompanyDataWithErrorBoundary from "../CompanyData/CompanyDataWithErrorBoundary";
import CompetitorsCardWithErrorBoundary from "../CompetitorsCard/CompetitorsCardWithErrorBoundary";
import DigitalAssetsCardWithErrorBoundary from "../DigitalAssetsCard/DigitalAssetsCardWithErrorBoundary";
import { ACTIVE_CARD_GEO } from "../GeoBreakdown/constants";
import { GeoBreakdownWithErrorBoundary } from "../GeoBreakdown/GeoBreakdownWithErrorBoundary";
import SignalsCardWithErrorBoundary from "../SignalsCard/SignalsCardWithErrorBoundary";
import TechnologiesCardWithErrorBoundary from "../TechnologiesCard/TechnologiesCardWithErrorBoundary";

const OverviewSectionRoot = () => {
    const { params } = useCurrentStateAndParamsWithFixedDomain();
    // GeoBreakdown defers its own query until it scrolls into view; only probe this query eagerly
    // when deep-linking straight to it (?activeCard=geo), so we know when to scroll to it.
    const { isFetched } = useGeoBreakdownQuery(params.key, { enabled: params.activeCard === ACTIVE_CARD_GEO });
    const geoRef = useRef<HTMLDivElement>(null);
    const hasScrolledRef = useRef(false);

    useEffect(() => {
        if (params.activeCard === ACTIVE_CARD_GEO && isFetched && !hasScrolledRef.current) {
            hasScrolledRef.current = true;
            geoRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
            proNavigator.updateQueryParams({ activeCard: null }, { reload: false, location: "replace" });
        }
    }, [params.activeCard, isFetched]);

    return (
        <>
            <CompanyCardWithErrorBoundary />
            <IndustryTaxonomyPromoBanner />
            <CompanyDataWithErrorBoundary />

            <Flex gap="24px">
                <SignalsCardWithErrorBoundary />
                <DigitalAssetsCardWithErrorBoundary />
            </Flex>
            <Flex gap="24px">
                <TechnologiesCardWithErrorBoundary />
                <CompetitorsCardWithErrorBoundary />
            </Flex>
            <GeoBreakdownWithErrorBoundary />
            <Box
                ref={geoRef}
                height="24px"
            />
        </>
    );
};

export default OverviewSectionRoot;
